import React from "react";
import { Button, Card } from "antd";
import {
    ArrowRight,
    PlayCircle,
    ShieldCheck,
    Lock,
    Truck,
    TrendingUp,
    Wallet,
    UploadCloud,
    Sparkles,
    Download,
    Users,
    Coins,
    IndianRupee,
    GitBranch,
} from "lucide-react";
import WebsiteHeader from "../Components/WebsiteHeader";
import WebsiteFooter from "../Components/WebsiteFooter";

export default function Home() {

     const handleRedirect = (url) => {
        // Sends the user to the specified external website URL
        window.location.href = url;
    };
    return (
        <div className="bg-[#fbfaff] text-slate-900">
            <WebsiteHeader />

            <section className="max-w-[1200px] mx-auto px-4 pt-1 pb-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="inline-flex items-center bg-purple-100 text-purple-700 px-4 py-2 rounded-full font-semibold text-sm mb-6">
                            🚀 Beta Launch - 30 Free Credits for First 30 Sellers
                        </div>

                        <h1 className="text-5xl md:text-6xl font-extrabold leading-tight">
                            Reduce Meesho Shipping Charges{" "}
                            <span className="text-purple-600">with AI</span>
                        </h1>

                        <p className="text-lg text-slate-600 mt-5 max-w-xl">
                            Upload your product image and let our AI find the lowest shipping
                            charge possible. Save more and sell more!
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 mt-8">
                            <Button
                            onClick={()=>handleRedirect('https://dashboard.shipless.in/')}
                                type="primary"
                                size="large"
                                className="bg-purple-600 h-14 px-8 font-bold"
                            >
                                Start Optimizing Now <ArrowRight size={18} />
                            </Button>

                            <Button size="large" className="h-14 px-8 font-bold">
                                <PlayCircle size={18} /> Watch Demo
                            </Button>
                        </div>

                        <div className="flex flex-wrap gap-6 mt-8 text-sm font-semibold text-slate-700">
                            <span className="flex items-center gap-2">
                                <ShieldCheck size={18} className="text-purple-600" />
                                Built for Meesho Sellers
                            </span>
                            <span className="flex items-center gap-2">
                                <ShieldCheck size={18} className="text-green-600" />
                                Secure & Private
                            </span>
                            <span className="flex items-center gap-2">
                                <Lock size={18} className="text-orange-500" />
                                No Image Sharing
                            </span>
                        </div>
                    </div>

                    <BeforeAfterDemo />

                </div>
            </section>

            <section className="max-w-[1200px] mx-auto px-4 py-8">
                <div className="bg-white rounded-2xl shadow-sm border p-8 grid grid-cols-1 md:grid-cols-5 gap-8 items-center">
                    <div className="md:col-span-1">
                        <h3 className="text-xl font-extrabold text-purple-600">
                            Made for Meesho Sellers, by a Meesho Seller
                        </h3>
                        <p className="text-slate-500">
                            We understand your problems because we face them every day.
                        </p>
                    </div>

                    <MiniStat icon={<Users />} value="50+" label="Beta Sellers Invited" />
                    <MiniStat icon={<Coins />} value="10" label="Credits Per Optimization" />
                    <MiniStat icon={<IndianRupee />} value="₹10 - ₹100+" label="Possible Shipping Difference" />
                    <MiniStat icon={<GitBranch />} value="3" label="Simple Steps" />
                </div>
            </section>

            <section id="features" className="max-w-[1200px] mx-auto px-4 py-10">
                <SectionTitle
                    title="Why Sellers Choose ShipLess AI"
                    desc="AI-powered image optimization to help you save on shipping and grow your business."
                />

                <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mt-8">
                    <FeatureCard icon={<Truck />} title="Lower Shipping Charges" desc="Our AI optimizes your product images to get the lowest possible shipping rate." />
                    <FeatureCard icon={<TrendingUp />} title="More Orders, More Sales" desc="Lower shipping makes your products more attractive and increases order chances." />
                    <FeatureCard icon={<Wallet />} title="Save Money Instantly" desc="Even ₹10 savings per order can make a big difference in your monthly profit." />
                    <FeatureCard icon={<ShieldCheck />} title="100% Safe & Secure" desc="Your images and data are completely safe with us. We never share your data." />
                </div>
            </section>

            <section id="how-it-works" className="max-w-[1000px] mx-auto px-4 py-8">
                <SectionTitle title="How It Works" desc="ShipLess AI works in 3 simple steps" />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-10">
                    <StepCard icon={<UploadCloud />} step="Step 1" title="Upload Product Image" desc="Upload a clear front side image of your Meesho product." />
                    <StepCard icon={<Sparkles />} step="Step 2" title="AI Optimizes" desc="Our AI creates multiple variations and finds the lowest shipping charge." />
                    <StepCard icon={<Download />} step="Step 3" title="Download & Save" desc="Download the optimized image and upload it on Meesho to start saving." />
                </div>
            </section>

            <section className="max-w-[1200px] mx-auto px-4 py-10">
                <SectionTitle title="Loved by Beta Sellers" desc="See what early users say about ShipLess AI" />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-8">
                    <Testimonial name="Pooja Sharma" text="I saved ₹60 on a single product! This tool is a game changer for Meesho sellers." />
                    <Testimonial name="Ravi Verma" text="Super easy to use and results are amazing. Even ₹10 savings helps in getting more orders." />
                    <Testimonial name="Neha Patel" text="Really helpful tool. I am using it for all my new listings now." />
                </div>
            </section>

            <section className="max-w-[1200px] mx-auto px-4 py-10">
                <div className="bg-gradient-to-r from-purple-700 to-purple-600 rounded-2xl p-8 text-white flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                    <div>
                        <h2 className="text-3xl font-extrabold">
                            Start Saving on Shipping Charges Today!
                        </h2>
                        <p className="mt-2">Join the beta and get 30 free credits instantly.</p>
                    </div>

                    <div>
                        <Button onClick={()=>handleRedirect('https://dashboard.shipless.in/')} size="large" className="h-14 px-10 font-bold">
                            Get 30 Free Credits <ArrowRight size={18} />
                        </Button>
                        <p className="text-sm text-purple-100 text-center mt-2">
                            No credit card required
                        </p>
                    </div>
                </div>
            </section>

            <WebsiteFooter />
        </div>
    );
}

function BeforeAfterDemo() {
    return (
        <div className="bg-[#f6f1fb] rounded-3xl p-5 shadow-xl max-w-[520px] mx-auto">
            {/* Original */}
            <div className="bg-white rounded-2xl border p-4">
                <p className="text-red-500 font-bold mb-3">
                    Original Image
                </p>

                <div className="flex gap-4">
                    <img
                        src="https://upload.meeshosupplyassets.com/cataloging/1781336880464/7xw62_512.png"
                        alt=""
                        className="w-20 h-20 rounded-lg object-cover"
                    />

                    <div>
                        <p className="text-slate-400 text-xs m-0">
                            Size: M,L,XL,XXL
                        </p>

                        <p className="text-slate-400 text-sm m-0 mt-2">
                            Meesho Price ₹199
                        </p>

                        <p className="text-slate-400 text-sm m-0">
                            Shipping (added separately) ₹119
                        </p>

                        <p className="text-2xl font-extrabold m-0 mt-1">
                            ₹318
                        </p>
                    </div>
                </div>
            </div>

            {/* Arrow */}
            <div className="flex items-center gap-4 my-1">
                <div className="flex-1 border-t border-purple-200" />

                <div className="w-5 h-5 rounded-full bg-white shadow flex items-center justify-center text-purple-600 text-1xl font-bold">
                    ↓
                </div>

              
                <div className="flex-1 border-t border-purple-200" />
            </div>

            {/* Optimized */}
            <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
                <p className="text-green-700 font-bold mb-3">
                    Optimized Image
                </p>

                <div className="flex gap-4">
                    <img
                        src="https://upload.meeshosupplyassets.com/cataloging/1781336944099/meesho-image-12.jpg"
                        alt=""
                        className="w-20 h-20 rounded-lg object-cover border-2 border-blue-500"
                    />

                    <div>
                        <p className="text-slate-400 text-xs m-0">
                            Size: M,L,XL,XXL
                        </p>

                        <p className="text-slate-400 text-sm m-0 mt-1">
                            Meesho Price ₹199
                        </p>

                        <p className="text-slate-400 text-sm m-0">
                            Shipping (added separately) ₹58
                        </p>

                        <p className="text-2xl font-extrabold m-0 mt-1">
                            ₹257
                        </p>
                    </div>
                </div>
            </div>

            {/* Bottom */}
            <div className="mt-5 bg-green-50 border border-green-200 rounded-2xl px-5 py-4 flex items-center justify-between">
                <div className="flex items-center gap-2 text-green-700 font-bold">
                    ↘ Shipping Reduced
                </div>

                <div className="text-green-700 font-bold">
                    Saved ₹61 🎉
                </div>
            </div>
        </div>
    );
}



function ImageCard({ title, amount, color, optimized }) {
    return (
        <div className="relative rounded-2xl overflow-hidden bg-purple-50 p-4 min-h-[360px] flex flex-col justify-between">
            <div className="absolute top-4 left-4 bg-white rounded-full px-4 py-2 text-xs font-bold">
                {title}
            </div>

            {optimized && (
                <div className="absolute top-4 right-4 bg-green-600 text-white rounded-full px-4 py-2 text-xs font-bold">
                    AI Optimized ✨
                </div>
            )}

            <div className="flex-1 flex items-center justify-center text-8xl">
                👗
            </div>

            <div className="bg-white rounded-xl p-4 shadow-sm">
                <p className="font-bold m-0">Shipping Charge</p>
                <h2
                    className={`text-4xl font-extrabold m-0 ${color === "green" ? "text-green-600" : "text-red-500"
                        }`}
                >
                    {amount}
                </h2>

                {optimized && (
                    <div className="mt-2 bg-green-100 text-green-700 px-3 py-2 rounded-lg font-bold text-sm">
                        You Save ₹60 (50%)
                    </div>
                )}
            </div>
        </div>
    );
}

function MiniStat({ icon, value, label }) {
    return (
        <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center">
                {icon}
            </div>
            <div>
                <h3 className="text-2xl font-extrabold m-0">{value}</h3>
                <p className="text-slate-500 m-0">{label}</p>
            </div>
        </div>
    );
}

function SectionTitle({ title, desc }) {
    return (
        <div className="text-center">
            <h2 className="text-3xl font-extrabold m-0">{title}</h2>
            <p className="text-slate-500 mt-3">{desc}</p>
        </div>
    );
}

function FeatureCard({ icon, title, desc }) {
    return (
        <Card className="rounded-2xl shadow-sm text-center h-full">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-purple-100 text-purple-600 flex items-center justify-center">
                {icon}
            </div>
            <h3 className="font-bold mt-5">{title}</h3>
            <p className="text-slate-500">{desc}</p>
        </Card>
    );
}

function StepCard({ icon, step, title, desc }) {
    return (
        <div className="text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-purple-100 text-purple-600 flex items-center justify-center">
                {icon}
            </div>
            <p className="text-purple-600 font-bold mt-4">{step}</p>
            <h3 className="font-bold">{title}</h3>
            <p className="text-slate-500">{desc}</p>
        </div>
    );
}

function Testimonial({ name, text }) {
    return (
        <Card className="rounded-2xl shadow-sm">
            <div className="flex gap-4">
                <div className="w-14 h-14 rounded-full bg-slate-200 flex items-center justify-center text-2xl">
                    👤
                </div>
                <div>
                    <p className="text-slate-700">“{text}”</p>
                    <h4 className="font-bold m-0">— {name}</h4>
                    <span className="text-xs bg-purple-100 text-purple-600 px-3 py-1 rounded-full">
                        Meesho Seller
                    </span>
                </div>
            </div>
        </Card>
    );
}